import { groupBy } from 'lodash';

export type OmitByValue<T, ValueType> = Pick<
  T,
  {
    [Key in keyof T]-?: T[Key] extends ValueType ? never : Key;
  }[keyof T]
>;

export type PossibleEntries<T> = OmitByValue<T, never>;
export function strongEntries<T extends Record<string, unknown>>(
  object: T,
): Array<[keyof PossibleEntries<T>, PossibleEntries<T>[keyof PossibleEntries<T>]]> {
  return Object.entries(object) as Array<
    [keyof PossibleEntries<T>, PossibleEntries<T>[keyof PossibleEntries<T>]]
  >;
}

export function strongKeys<T extends Record<string, unknown>>(object: T) {
  return Object.keys(object) as Array<keyof T>;
}

export function strongValues<T extends Record<string, unknown>>(object: T) {
  return Object.values(object) as Array<T[keyof T]>;
}

export function strongFromEntries<T extends string | number | symbol, K>(
  object: (readonly [T, K])[] | [T, K][],
): Record<T, K> {
  return Object.fromEntries(object) as Record<T, K>;
}

export function removeUndefinedKeys<T extends Record<string, unknown>>(object: T): T {
  return Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined)) as T;
}

export type WithRequired<TType, TFields extends keyof TType> = Omit<TType, TFields> &
  Required<Pick<TType, TFields>>;
export type WithSet<TType, TFields extends keyof TType> = Omit<TType, TFields> & {
  [P in TFields]-?: NonNullable<TType[P]>;
};

export type WithOptional<TType, TFields extends keyof TType> = Omit<TType, TFields> &
  Partial<Pick<TType, TFields>>;

export type NullableFields<TAttributes> = Omit<
  TAttributes,
  {
    [K in keyof TAttributes]-?: null extends TAttributes[K] ? never : K;
  }[keyof TAttributes]
>;

export type OptionalNullableFields<TAttributes> = Partial<NullableFields<TAttributes>> &
  Omit<TAttributes, keyof NullableFields<TAttributes>>;

export const strongGroupBy = <K extends string, V>(array: [K, V][], grouper: (a: [K, V]) => K) => {
  const grouped = groupBy(array, grouper);
  const extracted = Object.fromEntries(
    Object.entries(grouped).map(([k, pairs]) => [k, (pairs as [K, V][]).map(([, v]) => v)]),
  );
  return extracted as unknown as { [k in K]: V[] };
};
